import { useEffect, useState } from "react";
import { ExternalLink, Link2, FileText } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { PdfViewer } from "@/components/PdfViewer";

export default function Links() {
  const [site, setSite] = useState(null);
  const [activeDoc, setActiveDoc] = useState(null);
  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const links = site?.links || [];
  const cardClass =
    "group flex items-center gap-4 bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5 hover:shadow-lg hover:border-[#1E63C4] transition-all w-full text-left";

  const cardInner = (l) => (
    <>
      {l.image ? (
        <img src={fileUrl(l.image)} alt={l.title} className="h-14 w-14 rounded-lg object-contain shrink-0" />
      ) : (
        <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-[#1E63C4]/10 text-[#1E63C4] shrink-0">
          {l.pdf ? <FileText className="h-6 w-6" /> : <Link2 className="h-6 w-6" />}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="font-heading font-bold text-lg group-hover:text-[#1E63C4] transition-colors truncate">{l.title || l.url}</h3>
        {l.description && <p className="text-sm text-[#555555] mt-0.5 leading-relaxed">{l.description}</p>}
        {l.pdf && (
          <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#E11D2A]/10 text-[#E11D2A] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide" data-testid={`link-pdf-badge-${l.id}`}>
            <FileText className="h-3 w-3" /> PDF
          </span>
        )}
      </div>
      {l.pdf ? (
        <FileText className="h-5 w-5 text-[#1E63C4] shrink-0 group-hover:translate-x-0.5 transition-transform" />
      ) : (
        <ExternalLink className="h-5 w-5 text-[#1E63C4] shrink-0 group-hover:translate-x-0.5 transition-transform" />
      )}
    </>
  );

  return (
    <Layout>
      <Seo title="Billiards Links & Resources | Quinte BCA Pool League" description="Handy billiards links and resources for Quinte BCA players — FargoRate, BCAPL, league documents and more." path="/links" />
      <PageHeader eyebrow="Useful resources" title="Links" subtitle={site?.links_intro} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {!site ? (
          <p className="text-[#555555]">Loading links…</p>
        ) : links.length === 0 ? (
          <p className="text-[#555555]">Links are coming soon.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2" data-testid="links-grid">
            {links.map((l) =>
              l.pdf ? (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setActiveDoc({ url: l.pdf, title: l.title || "Document" })}
                  data-testid={`link-card-${l.id}`}
                  className={cardClass}
                >
                  {cardInner(l)}
                </button>
              ) : (
                <a
                  key={l.id}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`link-card-${l.id}`}
                  className={cardClass}
                >
                  {cardInner(l)}
                </a>
              )
            )}
          </div>
        )}
      </div>
      {activeDoc && <PdfViewer doc={activeDoc} onClose={() => setActiveDoc(null)} />}
    </Layout>
  );
}
